import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Helmet } from 'react-helmet-async';
import axiosInstance from "../utils/axiosInstance";

const Register = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    repeatPassword: "",
    currentLanguage: "",
    targetLanguage: "",
    level: "",
    bio: "",
  });

  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);

  // Client-Side Validation Function
  const validateForm = () => {
    const { firstName, lastName, username, email, password, repeatPassword } = form;

    if (!firstName || !lastName || !username || !email) {
      toast.error("All fields are required");
      return false;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return false;
    }

    if (password !== repeatPassword) {
      toast.error("Passwords do not match");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    return true;
  };

  // Check if email or username already exists
  const checkExisting = async () => {
    try {
      const emailRes = await axiosInstance.post(`${process.env.REACT_APP_API_BASE}/api/auth/check`, { email: form.email });
      if (emailRes.data.exists) {
        toast.error("Email already exists");
        return false;
      }

      const usernameRes = await axiosInstance.post(`${process.env.REACT_APP_API_BASE}/api/auth/check`, { username: form.username });
      if (usernameRes.data.exists) {
        toast.error("Username already exists");
        return false;
      }

      return true;
    } catch (err) {
      toast.error("Error checking username or email");
      return false;
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    if (!(await checkExisting())) return;

    try {
      await axiosInstance.post(`${process.env.REACT_APP_API_BASE}/api/auth/register`, form);
      setSuccess(true);
    } catch (err) {
      toast.error("Registration failed");
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-indigo-200 flex items-center justify-center px-4">
      <Helmet>
        <title>Register - PairLingo</title>
        <meta name="description" content="Speak with perfect language partner on PairLingo." />
        <meta name="keywords" content="language learning, pairlingo, chat, match, speaking chat, german language partner chat, language speaking practice" />
      </Helmet>
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-8">
        {success ? (
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold text-green-600">Registration Successful!</h2>
            <p className="text-gray-700">
              Your account has been created. Please check your email to activate your account.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="bg-indigo-600 text-white px-4 py-2 rounded font-semibold hover:bg-indigo-500"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-center text-indigo-700 mb-6">Create your account</h2>

            <form onSubmit={handleSubmit} className="space-y-4 text-sm">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={form.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded"
                required
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={form.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded"
                required
              />
              <input
                type="text"
                name="username"
                placeholder="Username"
                value={form.username}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded"
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded"
                required
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded"
                required
              />
              <input
                type="password"
                name="repeatPassword"
                placeholder="Repeat Password"
                value={form.repeatPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded"
                required
              />

              <select
                name="currentLanguage"
                value={form.currentLanguage}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded bg-white"
                required
              >
                <option value="">Select language you speak</option>
                {["English", "German", "Dutch", "Korean", "Spanish", "French", "Italian", "Japanese", "Korean", "Arabic", "Chinese", "Russian"].map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>

              <select
                name="targetLanguage"
                value={form.targetLanguage}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded bg-white"
                required
              >
                <option value="">Select language to learn</option>
                {["English", "German", "Dutch", "Korean", "Spanish", "French", "Italian", "Japanese", "Korean", "Arabic", "Chinese", "Russian"].map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>

              <select
                name="level"
                value={form.level}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded bg-white"
                required
              >
                <option value="">Select your level</option>
                {["A1", "A2", "B1", "B2", "C1", "C2"].map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>

              <textarea
                name="bio"
                placeholder="Tell us about yourself"
                value={form.bio}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border rounded resize-none"
              />

              <button
                type="submit"
                className="w-full bg-indigo-600 text-white py-2 rounded font-semibold hover:bg-indigo-500"
              >
                Register
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;
